
import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ExternalLink, Star } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { fetchRatings, fetchAllRatings, RatingSummary } from "@/services/ratingService";
import RatingDetailDialog from "./RatingDetailDialog";

const RatingWidget = () => {
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoadingAllRatings, setIsLoadingAllRatings] = useState(false);
  const [allRatingsData, setAllRatingsData] = useState<any>(null);
  
  const { data: ratingsData, isLoading, error } = useQuery({
    queryKey: ["ratings"],
    queryFn: fetchRatings,
  });
  
  // Show toast if there's an error
  useEffect(() => {
    if (error) {
      toast({
        title: "Error loading ratings",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    }
  }, [error, toast]);
  
  // Default values to show while loading
  let rating = 0;
  let reviewCount = 0;
  let ratingDistribution: { stars: number; percentage: number }[] = [];
  
  if (ratingsData) {
    rating = ratingsData.summary.average_rating;
    reviewCount = ratingsData.count;
    
    // Calculate percentages for the distribution
    const totalReviews = reviewCount;
    if (totalReviews > 0) {
      ratingDistribution = [
        { stars: 5, percentage: (ratingsData.summary["5-star"] / totalReviews) * 100 },
        { stars: 4, percentage: (ratingsData.summary["4-star"] / totalReviews) * 100 },
        { stars: 3, percentage: (ratingsData.summary["3-star"] / totalReviews) * 100 },
        { stars: 2, percentage: (ratingsData.summary["2-star"] / totalReviews) * 100 },
        { stars: 1, percentage: (ratingsData.summary["1-star"] / totalReviews) * 100 },
      ];
    }
  }
  
  const handleOpenRatingsDialog = async () => {
    setIsDialogOpen(true);
    
    // Only fetch all ratings if we haven't done so already
    if (!allRatingsData && !isLoadingAllRatings) {
      setIsLoadingAllRatings(true);
      try {
        const data = await fetchAllRatings();
        setAllRatingsData(data);
      } catch (error) {
        toast({
          title: "Error loading all ratings",
          description: error instanceof Error ? error.message : "An unknown error occurred",
          variant: "destructive",
        });
      } finally {
        setIsLoadingAllRatings(false);
      }
    }
  };
  
  return (
    <>
      <Card className="shadow-sm bg-white">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-xl font-bold text-gray-800">My Rating</CardTitle>
          <button 
            className="text-gray-600 hover:text-gray-800"
            onClick={handleOpenRatingsDialog}
          >
            <ExternalLink size={16} />
          </button>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Ratings are verified and are from people who use the DISCO service.
          </p>
          
          {isLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-12 bg-gray-200 rounded w-1/4 mx-auto mb-4"></div>
              <div className="h-6 bg-gray-200 rounded w-full"></div>
              <div className="h-6 bg-gray-200 rounded w-full"></div>
              <div className="h-6 bg-gray-200 rounded w-full"></div>
              <div className="h-6 bg-gray-200 rounded w-full"></div>
              <div className="h-6 bg-gray-200 rounded w-full"></div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-4">
              Failed to load ratings. Please try again later.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <div className="text-5xl font-bold text-gray-800">
                  {rating.toFixed(1)}
                </div>
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      size={16} 
                      className={i < Math.floor(rating) ? "text-neon-green fill-neon-green" : "text-gray-300"} 
                    />
                  ))}
                </div>
                <div className="text-sm text-gray-600">
                  {reviewCount}
                </div>
              </div>
              
              <div className="space-y-2">
                {ratingDistribution.map((item) => (
                  <div key={item.stars} className="flex items-center">
                    <span className="w-6 text-sm font-medium text-gray-700">{item.stars}</span>
                    <div className="flex-1 mx-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-neon-green rounded-full"
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              <button 
                className="w-full mt-6 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                onClick={handleOpenRatingsDialog}
              >
                View Reviews
              </button>
            </>
          )}
        </CardContent>
      </Card>
      
      <RatingDetailDialog 
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        ratings={(allRatingsData || ratingsData)?.results || []}
        averageRating={(allRatingsData || ratingsData)?.summary?.average_rating || 0}
        totalReviews={(allRatingsData || ratingsData)?.count || 0}
        distribution={ratingDistribution}
      />
    </>
  );
};

export default RatingWidget;
